import { useCallback } from "react";

import { useAppDispatch, useAppStore } from "app/redux-store-interactions";
import { bottomToolbarActions, selectActiveTab, selectIsExpanded } from "components/toolbars/mobile/slice";
import { coordinatesActions } from "features/coordinates";
import { measureActions } from "features/measure/measureSlice";
import { useObjectMeasureEntry } from "features/measure/useObjectMeasureEntry";
import { clippingOutlineLaserActions } from "features/outlineLaser";
import { pointLineActions } from "features/pointLine";
import { Picker, renderActions, selectPicker } from "features/render";
import { undoRedoActions } from "features/undoRedo";
import { selectIsProcoreDesign } from "slices/explorer";
import { ProcoreToolMode, procoreUxActions, selectActiveToolMode } from "slices/procoreUx";
import { getToolModeForPicker } from "slices/procoreUx/utils";

export function useEnterToolMode() {
    const dispatch = useAppDispatch();
    const store = useAppStore();
    const prepareObjectMeasureEntry = useObjectMeasureEntry();

    return useCallback(
        (params: { mode: ProcoreToolMode } | { picker: Picker }) => {
            const state = store.getState();
            const currentMode = selectActiveToolMode(state);
            const currentPicker = selectPicker(state);
            const isProcoreDesign = selectIsProcoreDesign(state);
            const isBottomToolbarExpandedBefore = selectIsExpanded(state);
            const activeTab = selectActiveTab(state);

            let picker = currentPicker;
            let mode = currentMode;
            if ("mode" in params) {
                mode = params.mode;
                const currentPickerBelongsToMode = getToolModeForPicker(currentPicker) === mode;
                if (!currentPickerBelongsToMode) {
                    picker = Picker.Object;
                }
            } else {
                picker = params.picker;
                mode = getToolModeForPicker(picker) ?? currentMode;
            }

            if (mode === currentMode && picker === currentPicker) {
                return;
            }

            const shouldSnapshot = currentMode === ProcoreToolMode.Default && mode !== currentMode && isProcoreDesign;
            const objectMeasureEntry = prepareObjectMeasureEntry(currentPicker, picker);

            const apply = () => {
                dispatch(procoreUxActions.setActiveToolMode(mode));
                dispatch(renderActions.setPicker(picker));
                if (!isBottomToolbarExpandedBefore) {
                    dispatch(bottomToolbarActions.expand());
                }
                dispatch(bottomToolbarActions.setActiveTab("tools"));
                if (shouldSnapshot) {
                    // Snapshot the current state once going into a tool mode
                    dispatch(pointLineActions.captureEditSessionStartSnapshot());
                    dispatch(clippingOutlineLaserActions.captureEditSessionStartSnapshot());
                    dispatch(coordinatesActions.captureEditSessionStartSnapshot());
                    dispatch(measureActions.captureObjectMeasureEditSessionStartSnapshot());
                    dispatch(renderActions.captureClippingEditSessionStartSnapshot());
                }
                objectMeasureEntry?.apply();
            };

            apply();

            dispatch(
                undoRedoActions.push({
                    description: "Enter tool mode",
                    undoCallback: () => {
                        dispatch(procoreUxActions.setActiveToolMode(currentMode));
                        dispatch(renderActions.setPicker(currentPicker));
                        if (!isBottomToolbarExpandedBefore) {
                            dispatch(bottomToolbarActions.collapse());
                        }
                        dispatch(bottomToolbarActions.setActiveTab(activeTab));
                        if (shouldSnapshot) {
                            dispatch(pointLineActions.clearEditSessionStartSnapshot());
                            dispatch(clippingOutlineLaserActions.clearEditSessionStartSnapshot());
                            dispatch(coordinatesActions.clearEditSessionStartSnapshot());
                            dispatch(measureActions.clearObjectMeasureEditSessionStartSnapshot());
                            dispatch(renderActions.clearClippingEditSessionStartSnapshot());
                        }
                        objectMeasureEntry?.undo();
                    },
                    redoCallback: apply,
                }),
            );
        },
        [dispatch, store, prepareObjectMeasureEntry],
    );
}
